import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * AdminBeacon — control panel for the homepage Beacon spotlight system.
 *
 * What admins can do here:
 *   • Globally pause every Beacon visual (homepage stays clean while
 *     paused; trophies on profiles stay visible).
 *   • Toggle automatic Beacon Boost (24h listing promotion for
 *     composite winners) on or off.
 *   • Tune the boost duration (1–168 hours).
 *   • Manually re-snapshot the previous week's winners (idempotent).
 *   • Force-refresh the spotlight cache for instant updates.
 */
export default function AdminBeacon() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [spotlight, setSpotlight] = useState(null);
  const [boosted, setBoosted] = useState([]);
  // Manual boost finder — username/email search.
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Local-only toggle controlling whether the "Currently boosted" section
  // is expanded. Persisted in localStorage so the admin's collapsed state
  // survives reloads. Default: ON (visible).
  const [boostedSectionOpen, setBoostedSectionOpen] = useState(() => {
    try {
      const v = window.localStorage.getItem("bidorah:admin:boostedOpen");
      return v === null ? true : v === "1";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "bidorah:admin:boostedOpen",
        boostedSectionOpen ? "1" : "0"
      );
    } catch { /* no-op */ }
  }, [boostedSectionOpen]);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/admin");
      return;
    }
    (async () => {
      try {
        const [s, sp, b] = await Promise.all([
          api.get("/admin/beacon/settings"),
          api.get("/beacon/spotlight"),
          api.get("/beacon/boosted"),
        ]);
        setSettings(s.data);
        setDraft(s.data);
        setSpotlight(sp.data);
        setBoosted(b.data?.items || []);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  const save = async () => {
    setWorking(true);
    try {
      const r = await api.post("/admin/beacon/settings", draft);
      setSettings(r.data);
      setDraft(r.data);
      toast.success("Beacon settings saved");
      const sp = await api.get("/beacon/spotlight");
      setSpotlight(sp.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  const refreshCache = async () => {
    setWorking(true);
    try {
      await api.get("/beacon/spotlight/refresh");
      const sp = await api.get("/beacon/spotlight");
      setSpotlight(sp.data);
      toast.success("Spotlight cache refreshed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  const snapshotNow = async () => {
    setWorking(true);
    try {
      const r = await api.post("/admin/beacon/snapshot");
      toast.success(
        r.data?.snapshotted
          ? `Snapshotted ${r.data.winners?.length || 0} winner(s)`
          : "Already snapshotted for this week"
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  // End a single seller's active boost immediately. Optimistically
  // remove them from the local list so the UI feels instant; refetch
  // on completion to stay in sync with the server.
  const endBoost = async (userId, username) => {
    if (!window.confirm(`End @${username}'s Beacon Boost now? They'll lose the 🌀 pill within minutes.`)) {
      return;
    }
    const prev = boosted;
    setBoosted((list) => list.filter((b) => b.user_id !== userId));
    try {
      await api.delete(`/admin/beacon/boost/${userId}`);
      toast.success(`Ended @${username}'s boost`);
    } catch (e) {
      setBoosted(prev); // rollback
      toast.error(apiError(e));
    }
  };

  const endAllBoosts = async () => {
    if (!boosted.length) return;
    if (!window.confirm(`End ALL ${boosted.length} active Beacon Boost(s) right now? This is platform-wide.`)) {
      return;
    }
    setWorking(true);
    try {
      const r = await api.delete("/admin/beacon/boost");
      toast.success(`Ended ${r.data?.ended || 0} boost(s)`);
      setBoosted([]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorking(false);
    }
  };

  // Debounced username/email search for the Manual Boost Manager.
  // Filters to seller accounts only — boosts don't apply to buyers.
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get("/admin/users", { params: { q, limit: 12 } })
        .then((r) => {
          const items = (r.data?.items || []).filter((u) =>
            (u.account_type || "").endsWith("_seller")
          );
          setSearchResults(items);
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 280);
    return () => clearTimeout(t);
  }, [searchQ]);

  // Refresh the local boosted list — used after granting/ending a manual boost.
  const refreshBoosted = async () => {
    try {
      const r = await api.get("/beacon/boosted");
      setBoosted(r.data?.items || []);
    } catch { /* no-op */ }
  };

  // Compute "is currently boosted" for a result row — read directly
  // from the global boosted set so the toggle reflects truth.
  const isBoosted = (uid) => boosted.some((b) => b.user_id === uid);

  const grantBoost = async (u) => {
    try {
      await api.post(`/admin/beacon/boost/${u.user_id}`);
      toast.success(`Boosted @${u.username || u.name}`);
      await refreshBoosted();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const revokeBoost = async (u) => {
    try {
      await api.delete(`/admin/beacon/boost/${u.user_id}`);
      toast.success(`Ended @${u.username || u.name}'s boost`);
      await refreshBoosted();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  if (!user || user.role !== "admin") return null;
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-5xl mx-auto px-6 py-16 text-zinc-500">
          Loading Beacon controls…
        </main>
        <Footer />
      </div>
    );
  }

  const dirty =
    settings &&
    draft &&
    JSON.stringify(settings) !== JSON.stringify(draft);

  return (
    <div
      className="min-h-screen flex flex-col bg-white"
      data-testid="admin-beacon-page"
    >
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 lg:px-8 py-12">
        <Link to="/admin" className="text-sm text-zinc-500 hover:text-[#1D78AB]">
          ← Admin
        </Link>
        <p className="overline text-zinc-500 mt-3">Admin · Beacon</p>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2">
          🌀 Beacon Spotlight Controls
        </h1>
        <p className="text-zinc-600 mt-3 max-w-2xl">
          Pause the entire Beacon system, tune the Beacon Boost, or manually
          re-snapshot the prior week's winners. <b>Sellers are OFF the
          Spotlight by default</b> — opt them in from <Link to="/admin" className="text-[#1D78AB] underline">Admin → Users → Edit</Link> (tick "🌀 Allow on Beacon Spotlight").
        </p>

        {/* Live status */}
        <section
          className="mt-10 rounded-2xl border border-zinc-200 bg-zinc-50 p-6"
          data-testid="admin-beacon-status"
        >
          <p className="overline text-zinc-500">Right now</p>
          <div className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Status</div>
              <div className={`mt-1 font-display font-black text-2xl ${settings.enabled ? "text-emerald-600" : "text-rose-600"}`}>
                {settings.enabled ? "Live" : "Paused"}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Today's category</div>
              <div className="mt-1 font-display font-black text-2xl text-[#1D78AB]">
                {spotlight?.category_label || "—"}
              </div>
              <div className="text-xs text-zinc-500">{spotlight?.today}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Top 10 surfaced</div>
              <div className="mt-1 font-display font-black text-2xl">{spotlight?.top_10?.length || 0}</div>
            </div>
          </div>
        </section>

        {/* Toggles */}
        <section className="mt-8 space-y-4">
          <h2 className="font-display font-bold text-2xl">Global controls</h2>

          <label
            className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${
              draft.enabled ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"
            }`}
            data-testid="admin-beacon-toggle-enabled"
          >
            <input
              type="checkbox"
              checked={!!draft.enabled}
              onChange={(e) =>
                setDraft((d) => ({ ...d, enabled: e.target.checked }))
              }
              className="mt-1 h-5 w-5 accent-emerald-600"
              data-testid="admin-beacon-enabled-checkbox"
            />
            <div className="flex-1">
              <div className="font-bold">Beacon Spotlight enabled</div>
              <div className="text-xs text-zinc-600 mt-1 leading-snug">
                When OFF, every homepage Beacon visual disappears: the Spotlight
                wall, Coliseum Ribbon, Headliner Stage, Pulse Ring, and Velocity
                Lane all stop rendering. Trophies on profiles stay visible.
              </div>
            </div>
          </label>

          <label
            className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer ${
              draft.boost_enabled ? "border-amber-300 bg-amber-50" : "border-zinc-300 bg-zinc-50"
            }`}
            data-testid="admin-beacon-toggle-boost"
          >
            <input
              type="checkbox"
              checked={!!draft.boost_enabled}
              onChange={(e) =>
                setDraft((d) => ({ ...d, boost_enabled: e.target.checked }))
              }
              className="mt-1 h-5 w-5 accent-amber-600"
              data-testid="admin-beacon-boost-checkbox"
            />
            <div className="flex-1">
              <div className="font-bold">Beacon Boost auto-promotion</div>
              <div className="text-xs text-zinc-600 mt-1 leading-snug">
                Composite weekly winners get a 24-hour "🌀 Beacon Boost" pill
                on their listings + seller card and rank higher in the
                category. Disable to award trophies without the boost.
              </div>
            </div>
          </label>

          <div className="flex items-center gap-3 p-4 rounded-xl border border-zinc-200" data-testid="admin-beacon-boost-duration-row">
            <label className="text-sm font-semibold w-48">Boost duration (hours)</label>
            <Input
              type="number"
              min={1}
              max={168}
              value={draft.boost_duration_hours}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  boost_duration_hours: Math.max(
                    1,
                    Math.min(168, parseInt(e.target.value || "24", 10) || 24)
                  ),
                }))
              }
              className="w-24"
              data-testid="admin-beacon-boost-duration"
            />
            <span className="text-xs text-zinc-500">1 – 168 (one week)</span>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button
              disabled={!dirty || working}
              onClick={save}
              className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
              data-testid="admin-beacon-save"
            >
              {working ? "Saving…" : "Save settings"}
            </Button>
            <Button
              variant="outline"
              onClick={refreshCache}
              disabled={working}
              data-testid="admin-beacon-refresh"
            >
              Force-refresh cache
            </Button>
            <Button
              variant="outline"
              onClick={snapshotNow}
              disabled={working}
              data-testid="admin-beacon-snapshot"
            >
              Snapshot previous week
            </Button>
          </div>
        </section>

        {/* Manual Boost Manager — find any seller by username/email/name
            and toggle their Beacon Boost on or off in a single click.
            Useful for hand-picking promo windows, partnership pushes,
            and new-seller onboarding without waiting for the weekly snapshot. */}
        <section className="mt-12" data-testid="admin-beacon-manual-boost">
          <h2 className="font-display font-bold text-2xl">Manual boost manager</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Search any seller by username, email, or name. Toggle Boost on
            (or off) directly from the result row. Granted boosts use the
            duration you set above.
          </p>

          <div className="mt-4 relative">
            <Input
              type="text"
              placeholder="Search by @username, email, or name…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              className="pr-10"
              data-testid="admin-beacon-search-input"
              autoComplete="off"
            />
            {searching && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 uppercase tracking-widest">
                …
              </span>
            )}
            {searchQ && !searching && (
              <button
                type="button"
                onClick={() => setSearchQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700 text-sm"
                aria-label="Clear search"
                data-testid="admin-beacon-search-clear"
              >
                ✕
              </button>
            )}
          </div>

          {searchQ.trim().length >= 2 && (
            <div className="mt-3 space-y-2" data-testid="admin-beacon-search-results">
              {searchResults.length === 0 && !searching && (
                <p className="text-zinc-500 text-sm italic px-1">
                  No sellers match "{searchQ}".
                </p>
              )}
              {searchResults.map((u) => {
                const boosted = isBoosted(u.user_id);
                return (
                  <div
                    key={u.user_id}
                    className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border ${
                      boosted
                        ? "border-amber-300 bg-amber-50"
                        : "border-zinc-200 bg-white hover:bg-zinc-50"
                    }`}
                    data-testid={`admin-beacon-search-row-${u.user_id}`}
                  >
                    <Link
                      to={`/seller/${u.user_id}`}
                      className="flex-1 min-w-0 flex items-center gap-3 hover:opacity-80"
                    >
                      <div className="h-9 w-9 bg-zinc-100 rounded-full grid place-items-center font-semibold text-sm shrink-0">
                        {(u.username || u.name || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold truncate">
                          @{u.username || u.name}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">
                          {u.email} · {u.account_type?.replace("_", " ")}
                        </div>
                      </div>
                    </Link>
                    {boosted ? (
                      <button
                        type="button"
                        onClick={() => revokeBoost(u)}
                        data-testid={`admin-beacon-search-revoke-${u.user_id}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-zinc-900 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-bold transition"
                      >
                        🌀 Boost ON — click to end
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => grantBoost(u)}
                        data-testid={`admin-beacon-search-grant-${u.user_id}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-300 hover:bg-amber-200 text-amber-950 px-3 py-1.5 text-xs font-black transition"
                      >
                        🌀 Boost OFF — grant boost
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Boosted right now — collapsible. Admin can toggle the entire
            section on/off; preference persists across reloads via the
            `bidorah:admin:boostedOpen` localStorage key. */}
        <section className="mt-12" data-testid="admin-beacon-boosted-section">
          <button
            type="button"
            onClick={() => setBoostedSectionOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 text-left group"
            data-testid="admin-beacon-boosted-toggle"
            aria-expanded={boostedSectionOpen}
          >
            <div>
              <h2 className="font-display font-bold text-2xl group-hover:text-[#1D78AB] transition-colors">
                Currently boosted ({boosted.length})
              </h2>
              {boostedSectionOpen && (
                <p className="text-sm text-zinc-500 mt-1">
                  Sellers with an active Beacon Boost. Their listings show
                  the "🌀 Beacon Boost" pill until the timer expires.
                </p>
              )}
            </div>
            <span
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-widest transition-colors ${
                boostedSectionOpen
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                  : "bg-zinc-50 border-zinc-300 text-zinc-600"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  boostedSectionOpen ? "bg-emerald-500" : "bg-zinc-400"
                }`}
              />
              {boostedSectionOpen ? "Showing" : "Hidden"}
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                className={`transition-transform ${
                  boostedSectionOpen ? "rotate-180" : "rotate-0"
                }`}
                aria-hidden="true"
              >
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
              </svg>
            </span>
          </button>

          {boostedSectionOpen && (
            <>
              {boosted.length > 0 && (
                <div className="mt-3">
                  <Button
                    variant="outline"
                    onClick={endAllBoosts}
                    disabled={working}
                    className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                    data-testid="admin-beacon-end-all-boosts"
                  >
                    End all {boosted.length} active boost(s)
                  </Button>
                </div>
              )}
              <div className="mt-4 space-y-2" data-testid="admin-beacon-boosted-list">
                {boosted.length === 0 && (
                  <p className="text-zinc-500 text-sm italic">
                    No active boosts right now. Composite winners earn one
                    automatically on the next snapshot.
                  </p>
                )}
                {boosted.map((b) => (
                  <div
                    key={b.user_id}
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50"
                    data-testid={`admin-beacon-boosted-${b.user_id}`}
                  >
                    <Link
                      to={`/seller/${b.user_id}`}
                      className="flex-1 min-w-0 hover:opacity-80"
                    >
                      <div className="font-bold truncate">@{b.username}</div>
                      <div className="text-xs text-zinc-600">
                        {b.beacon_boost_reason || "Composite winner"}
                      </div>
                    </Link>
                    <div className="text-xs text-zinc-500 font-mono shrink-0 hidden sm:block">
                      expires {new Date(b.beacon_boost_until).toLocaleString()}
                    </div>
                    <button
                      type="button"
                      onClick={() => endBoost(b.user_id, b.username)}
                      data-testid={`admin-beacon-end-boost-${b.user_id}`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full bg-zinc-900 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-bold transition"
                    >
                      End boost
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
